// ── Typed API client for WhatsApp inbox ────────────────────────────
//
// Thin wrappers over `@/lib/api` so the components don't pass raw
// path strings around. One place to evolve when the backend API
// shape shifts.

import api from '@/lib/api';

// ── Types ───────────────────────────────────────────────────────────

export type ConversationStatus = 'OPEN' | 'SNOOZED' | 'CLOSED' | 'ARCHIVED';

export interface LastMessagePreview {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  messageType: string;
  bodyPreview: string | null;
  timestamp: string;
}

export interface ConversationRow {
  id: string;
  whatsappAccountId: string;
  tenantId: string;
  contactWaId: string;
  contactName: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  sessionExpiresAt: string | null;
  unreadCount: number;
  status: ConversationStatus;
  assignedAgentId: string | null;
  leadId: string | null;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
  assignedAgent: { id: string; name: string; email: string } | null;
  lead: { id: string; name: string; status: string } | null;
  lastMessage: LastMessagePreview | null;
}

export interface ConversationsListResponse {
  data: ConversationRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ConversationDetailResponse extends ConversationRow {
  account: {
    id: string;
    displayPhoneNumber: string;
    verifiedName: string | null;
    status: string;
  };
  messages: WhatsappMessage[];
  internalNotes: InternalNote[];
  sessionStatus: {
    isOpen: boolean;
    expiresAt: string | null;
    remainingMs: number;
    remainingHours: number;
    mustUseTemplate: boolean;
  };
}

export interface WhatsappMessage {
  id: string;
  tenantId: string;
  whatsappAccountId: string;
  conversationId: string;
  waMessageId: string | null;
  direction: 'INBOUND' | 'OUTBOUND';
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  messageType: string;
  content: any;
  bodyPreview: string | null;
  mediaUrl: string | null;
  mediaMimeType: string | null;
  mediaSize: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  sentByUserId: string | null;
  replyToMessageId: string | null;
  broadcastId: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failedAt: string | null;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface InternalNote {
  id: string;
  tenantId: string;
  conversationId: string;
  authorUserId: string | null;
  authorName: string;
  body: string;
  createdAt: string;
  author?: { id: string; name: string; email: string } | null;
}

export interface WhatsappAccountListItem {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string | null;
  status: string;
  qualityRating: string;
  defaultReengagementTemplateId?: string | null;
}

export interface SendMessageBody {
  whatsappAccountId: string;
  toWaId: string;
  contactName?: string;
  type:
    | 'text'
    | 'image'
    | 'video'
    | 'document'
    | 'audio'
    | 'template'
    | 'interactive'
    | 'location'
    | 'contacts';
  content: Record<string, unknown>;
  idempotencyKey?: string;
  replyToWaMessageId?: string;
}

export interface SendMessageResponse {
  messageId: string;
  status: 'queued' | 'duplicate';
  templateFallback?: { templateName: string; language: string };
}

// ── Accounts ──────────────────────────────────────────────────────

export async function listWhatsappAccounts(): Promise<WhatsappAccountListItem[]> {
  const res = await api.get('/whatsapp/accounts');
  return res.data;
}

// ── Conversations ─────────────────────────────────────────────────

/** One-time migration: imports WhatsAppMessageLegacy rows into WhatsappConversation. */
export async function migrateLegacyConversations(): Promise<{ conversations: number; messages: number }> {
  const res = await api.post('/whatsapp/conversations/migrate-legacy');
  return res.data;
}

export async function listConversations(params: {
  whatsappAccountId?: string;
  status?: ConversationStatus;
  assignedAgentId?: string;
  search?: string;
  hasUnread?: boolean;
  leadId?: string;
  page?: number;
  limit?: number;
}): Promise<ConversationsListResponse> {
  const res = await api.get('/whatsapp/conversations', { params });
  return res.data;
}

export async function getConversation(id: string): Promise<ConversationDetailResponse> {
  const res = await api.get(`/whatsapp/conversations/${id}`);
  return res.data;
}

export async function getMessages(
  id: string,
  cursor?: string,
  limit = 50,
): Promise<{ data: WhatsappMessage[]; nextCursor: string | null }> {
  const res = await api.get(`/whatsapp/conversations/${id}/messages`, {
    params: { cursor, limit },
  });
  return res.data;
}

export async function assignConversation(id: string, agentId: string) {
  const res = await api.post(`/whatsapp/conversations/${id}/assign`, { agentId });
  return res.data;
}

export async function unassignConversation(id: string) {
  const res = await api.post(`/whatsapp/conversations/${id}/unassign`);
  return res.data;
}

export async function updateConversationStatus(id: string, status: ConversationStatus) {
  const res = await api.patch(`/whatsapp/conversations/${id}/status`, { status });
  return res.data;
}

export async function linkLead(id: string, leadId: string) {
  const res = await api.post(`/whatsapp/conversations/${id}/link-lead`, { leadId });
  return res.data;
}

export async function unlinkLead(id: string) {
  const res = await api.post(`/whatsapp/conversations/${id}/unlink-lead`);
  return res.data;
}

export async function addInternalNote(id: string, body: string): Promise<InternalNote> {
  const res = await api.post(`/whatsapp/conversations/${id}/notes`, { body });
  return res.data;
}

export async function listInternalNotes(id: string): Promise<InternalNote[]> {
  const res = await api.get(`/whatsapp/conversations/${id}/notes`);
  return res.data;
}

export async function markConversationRead(id: string, sendReadReceipts = true) {
  const res = await api.post(`/whatsapp/conversations/${id}/mark-read`, {
    sendReadReceipts,
  });
  return res.data;
}

// ── Send ───────────────────────────────────────────────────────────

export async function sendMessage(body: SendMessageBody): Promise<SendMessageResponse> {
  const res = await api.post('/whatsapp/messages', body);
  return res.data;
}

// ── Users (for assignment dropdown) ───────────────────────────────

export interface UserOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

export async function listAgents(): Promise<UserOption[]> {
  // Reuse the existing users endpoint. Filters out inactive users.
  // UserService.findAll returns { users: [...], total, page, ... } so read
  // res.data.users first; fall back to res.data.data / res.data for safety.
  // limit=200 ensures all team members appear (default page size is 25).
  const res = await api.get('/users', { params: { isActive: true, limit: 200 } });
  const users = Array.isArray(res.data?.users)
    ? res.data.users
    : Array.isArray(res.data?.data)
      ? res.data.data
      : Array.isArray(res.data)
        ? res.data
        : [];
  return users.map((u: any) => ({
    id: u.id,
    name: u.name ?? u.email,
    email: u.email,
    role: u.role ?? 'agent',
  }));
}

// ── Leads search (for link-lead modal) ────────────────────────────

export interface LeadSearchResult {
  id: string;
  name: string;
  status: string;
  phones?: { phone: string }[];
}

export async function searchLeads(query: string): Promise<LeadSearchResult[]> {
  // POST so the search term (PII) goes in the body, not the URL.
  const res = await api.post('/leads/search', { search: query, limit: 10 });
  const leads = Array.isArray(res.data?.leads) ? res.data.leads : Array.isArray(res.data) ? res.data : [];
  return leads.map((l: any) => ({
    id: l.id,
    name: l.name,
    status: l.status,
    phones: l.phones,
  }));
}

// ── Accounts (detailed — for Settings page) ───────────────────────

export interface WhatsappAccountDetail {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string | null;
  status: string;
  qualityRating: string;
  wabaId: string;
  phoneNumberId: string;
  tokenExpiresAt: string | null;
  autoAssignEnabled: boolean;
  sessionWindowHours: number;
  createdAt: string;
  updatedAt: string;
}

export async function getWhatsappAccount(id: string): Promise<WhatsappAccountDetail> {
  const res = await api.get(`/whatsapp/accounts/${id}`);
  return res.data;
}

export async function refreshWhatsappToken(id: string) {
  const res = await api.post(`/whatsapp/accounts/${id}/refresh-token`);
  return res.data;
}

export async function disconnectWhatsappAccount(id: string) {
  const res = await api.delete(`/whatsapp/accounts/${id}`);
  return res.data;
}

export async function updateWhatsappAccount(
  id: string,
  data: { autoAssignEnabled?: boolean; sessionWindowHours?: number; defaultReengagementTemplateId?: string | null },
) {
  const res = await api.patch(`/whatsapp/accounts/${id}`, data);
  return res.data;
}

// ── Onboarding / Embedded Signup ─────────────────────────────────

export interface CompleteOnboardingBody {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  pin: string;
}

export async function completeOnboarding(body: CompleteOnboardingBody) {
  const res = await api.post('/whatsapp/onboarding/complete', body);
  return res.data;
}

// ── Templates ─────────────────────────────────────────────────────

export type TemplateStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAUSED'
  | 'DISABLED'
  | 'DELETED';
export type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

export interface WhatsappTemplate {
  id: string;
  tenantId: string;
  whatsappAccountId: string;
  metaTemplateId: string | null;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  components: any[];
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplatesListResponse {
  data: WhatsappTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function listTemplates(params: {
  whatsappAccountId?: string;
  status?: TemplateStatus;
  category?: TemplateCategory;
  language?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<TemplatesListResponse> {
  const res = await api.get('/whatsapp/templates', { params });
  return res.data;
}

export async function getTemplate(id: string): Promise<WhatsappTemplate> {
  const res = await api.get(`/whatsapp/templates/${id}`);
  return res.data;
}

export async function createTemplate(body: {
  whatsappAccountId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  components: any[];
}): Promise<WhatsappTemplate> {
  const res = await api.post('/whatsapp/templates', body);
  return res.data;
}

export async function deleteTemplate(id: string) {
  const res = await api.delete(`/whatsapp/templates/${id}`);
  return res.data;
}

export async function syncTemplates(whatsappAccountId: string) {
  const res = await api.post('/whatsapp/templates/sync', { whatsappAccountId });
  return res.data;
}

// ── Broadcasts ────────────────────────────────────────────────────

export type BroadcastStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELED'
  | 'FAILED';

export interface BroadcastRow {
  id: string;
  tenantId: string;
  whatsappAccountId: string;
  name: string;
  description: string | null;
  status: BroadcastStatus;
  templateId: string;
  audienceFilter: Record<string, unknown>;
  variableMapping: Record<string, { type: 'attribute' | 'static'; value: string }>;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  template?: WhatsappTemplate;
}

export interface BroadcastsListResponse {
  data: BroadcastRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function listBroadcasts(params: {
  whatsappAccountId?: string;
  status?: BroadcastStatus;
  page?: number;
  limit?: number;
}): Promise<BroadcastsListResponse> {
  const res = await api.get('/whatsapp/broadcasts', { params });
  return res.data;
}

export async function getBroadcast(id: string): Promise<BroadcastRow> {
  const res = await api.get(`/whatsapp/broadcasts/${id}`);
  return res.data;
}

export async function getBroadcastStats(id: string): Promise<{
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  progressPct: number;
}> {
  const res = await api.get(`/whatsapp/broadcasts/${id}/stats`);
  return res.data;
}

export async function getBroadcastRecipients(
  id: string,
  params: { status?: string; search?: string; page?: number; limit?: number },
) {
  const res = await api.get(`/whatsapp/broadcasts/${id}/recipients`, { params });
  return res.data;
}

export async function createBroadcast(body: {
  whatsappAccountId: string;
  name: string;
  description?: string;
  templateId: string;
  audienceFilter: Record<string, unknown>;
  variableMapping: Record<string, { type: 'attribute' | 'static'; value: string }>;
}): Promise<BroadcastRow> {
  const res = await api.post('/whatsapp/broadcasts', body);
  return res.data;
}

export async function updateBroadcast(
  id: string,
  body: Partial<{
    name: string;
    description: string;
    templateId: string;
    audienceFilter: Record<string, unknown>;
    variableMapping: Record<string, { type: 'attribute' | 'static'; value: string }>;
  }>,
): Promise<BroadcastRow> {
  const res = await api.patch(`/whatsapp/broadcasts/${id}`, body);
  return res.data;
}

export async function deleteBroadcast(id: string) {
  const res = await api.delete(`/whatsapp/broadcasts/${id}`);
  return res.data;
}

export async function previewBroadcastAudience(id: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/preview`);
  return res.data as {
    eligibleCount: number;
    excludedCount: number;
    excludedBreakdown: Record<string, number>;
    estimatedCostUsd: number | null;
    sampleRecipient: { waId: string; name: string | null } | null;
  };
}

export async function launchBroadcast(id: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/launch`);
  return res.data;
}

export async function scheduleBroadcast(id: string, scheduledFor: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/schedule`, { scheduledFor });
  return res.data;
}

export async function pauseBroadcast(id: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/pause`);
  return res.data;
}

export async function resumeBroadcast(id: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/resume`);
  return res.data;
}

export async function cancelBroadcast(id: string) {
  const res = await api.post(`/whatsapp/broadcasts/${id}/cancel`);
  return res.data;
}

// ── Integration Settings ──────────────────────────────────────────

export interface IntegrationSettings {
  metaAppId: string | null;
  metaAppSecretSet: boolean;
  metaConfigId: string | null;
  source: 'tenant' | 'platform' | 'env';
}

export async function getIntegrationSettings(): Promise<IntegrationSettings> {
  const res = await api.get('/whatsapp/integration-settings');
  return res.data;
}

export async function updateIntegrationSettings(body: {
  metaAppId?: string | null;
  metaAppSecret?: string | null;
  metaConfigId?: string | null;
}): Promise<IntegrationSettings> {
  const res = await api.patch('/whatsapp/integration-settings', body);
  return res.data;
}
